import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react'
import { InteractionRequiredAuthError } from '@azure/msal-browser'
import { msalInstance } from './security/hooks/authProvider'
import { graphPhotoRequest, graphMeRequest } from './security/authConfig'

const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

// A landing page can render dozens of engagement cards, each with several team
// members - without a limit every one of them would fire its Graph photo
// request at once. Cap it to 3 in-flight lanes, each pausing 500ms after a
// fetch completes before picking up its next queued user.
const MAX_CONCURRENT_PHOTO_FETCHES = 3
const PHOTO_FETCH_COOLDOWN_MS = 500

let activeLanes = 0
const photoFetchQueue: Array<() => void> = []

const runThrottled = <T>(task: () => Promise<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    const run = () => {
      activeLanes++
      task()
        .then(resolve, reject)
        .finally(() => {
          setTimeout(() => {
            activeLanes--
            photoFetchQueue.shift()?.()
          }, PHOTO_FETCH_COOLDOWN_MS)
        })
    }
    if (activeLanes < MAX_CONCURRENT_PHOTO_FETCHES) {
      run()
    } else {
      photoFetchQueue.push(run)
    }
  })

const acquireGraphToken = async (scopeRequest: { scopes: string[] } = graphPhotoRequest): Promise<string | null> => {
  const account = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0]
  if (!account) return null

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent({ ...scopeRequest, account })
    return tokenResponse.accessToken
  } catch (err) {
    // User.ReadBasic.All hasn't been consented yet - fall back to initials/no-tooltip rather than looping on this.
    if (err instanceof InteractionRequiredAuthError) return null
    throw err
  }
}

const fetchUserPhoto = async (adIdentifier: string): Promise<string | null> => {
  const accessToken = await acquireGraphToken()
  if (!accessToken) return null

  const response = await fetch(`https://graph.microsoft.com/v1.0/users/${encodeURIComponent(adIdentifier)}/photo/$value`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  if (!response.ok) return null

  return blobToDataUrl(await response.blob())
}

const fetchMyPhoto = async (): Promise<string | null> => {
  // "me" only needs the base User.Read scope - no admin consent required, unlike by-id lookups.
  const accessToken = await acquireGraphToken(graphMeRequest)
  if (!accessToken) return null

  const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  if (!response.ok) return null

  return blobToDataUrl(await response.blob())
}

const fetchUserEmail = async (adIdentifier: string): Promise<string | null> => {
  const accessToken = await acquireGraphToken()
  if (!accessToken) return null

  const response = await fetch(
    `https://graph.microsoft.com/v1.0/users/${encodeURIComponent(adIdentifier)}?$select=mail,userPrincipalName`,
    { headers: { Authorization: `Bearer ${accessToken}` } },
  )
  if (!response.ok) return null

  const data: { mail?: string | null; userPrincipalName?: string | null } = await response.json()
  return data.mail ?? data.userPrincipalName ?? null
}

// Separate RTK Query slice (not the backend sodApi) so photo lookups go straight
// to Graph from the browser. RTK Query's cache is keyed by the adIdentifier arg,
// so each unique user is only ever fetched once per session.
export const graphApi = createApi({
  reducerPath: 'graphApi',
  baseQuery: fakeBaseQuery<string>(),
  keepUnusedDataFor: 3600,
  endpoints: (builder) => ({
    getUserPhoto: builder.query<string | null, string>({
      queryFn: async (adIdentifier) => {
        try {
          return { data: await runThrottled(() => fetchUserPhoto(adIdentifier)) }
        } catch (err) {
          return { error: err instanceof Error ? err.message : 'Failed to fetch user photo' }
        }
      },
    }),
    getUserEmail: builder.query<string | null, string>({
      queryFn: async (adIdentifier) => {
        try {
          return { data: await runThrottled(() => fetchUserEmail(adIdentifier)) }
        } catch (err) {
          return { error: err instanceof Error ? err.message : 'Failed to fetch user email' }
        }
      },
    }),
    getMyPhoto: builder.query<string | null, void>({
      queryFn: async () => {
        // Single header-level request - must not queue behind bulk engagement-card fetches.
        try {
          return { data: await fetchMyPhoto() }
        } catch (err) {
          return { error: err instanceof Error ? err.message : 'Failed to fetch own photo' }
        }
      },
    }),
  }),
})

export const { useGetUserPhotoQuery, useGetUserEmailQuery, useGetMyPhotoQuery } = graphApi
